import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Music2,
  Volume2,
  Flower2,
  Sparkles,
  PartyPopper,
  ArrowUpRight,
} from "lucide-react";

const services = [
  {
    key: "dj",
    title: "Professional DJ Entertainment",
    short: "DJ Services",
    desc: "From Top 40, hip-hop, Bollywood and Punjabi to Latin and EDM, our DJs read the room and keep your guests moving all night long. Full sound system, professional lighting, and seamless transitions tailored to your event's vibe.",
    icon: Music2,
    img: "https://customer-assets.emergentagent.com/job_karnival-preview/artifacts/ml2gpzc0_IMG_1070.JPG",
    span: "md:col-span-6 md:row-span-1",
    to: "/dj-avtaar",
  },
  {
    key: "decor",
    title: "Custom Event Décor",
    short: "Event Décor",
    desc: "Immersive environments that match your vision — floral centrepieces, balloon installations, themed backdrops, and custom signage, tailored to your style.",
    icon: Flower2,
    img: "https://customer-assets.emergentagent.com/job_karnival-preview/artifacts/dtmd93wa_IMG_1451.jpg",
    span: "md:col-span-6 md:row-span-1",
    to: "/gallery?filter=decor",
    imgClass:
      "absolute inset-0 w-full h-full object-cover object-[50%_center] group-hover:scale-[1.04] transition-transform duration-[1500ms] ease-out",
  },
  {
    key: "av",
    title: "Audio-Visual Production",
    short: "AV Setup",
    desc: "Crystal-clear sound and stunning visuals — speakers, microphones, projectors, LED screens, and intelligent lighting rigs for events of any size.",
    icon: Volume2,
    img: "https://images.unsplash.com/photo-1639682687092-0264e96cbb71",
    span: "md:col-span-4 md:row-span-1",
    to: "/gallery?filter=av",
  },
  {
    key: "effects",
    title: "Spectacular Special Effects",
    short: "Special Effects",
    desc: "Cold spark fountains, CO₂ cannons, confetti blasts, fog machines — synced to your music and choreographed for maximum impact at just the right moment.",
    icon: Sparkles,
    img: "https://customer-assets.emergentagent.com/job_karnival-preview/artifacts/qifmu7od_IMG_9683.JPG",
    span: "md:col-span-4 md:row-span-1",
    to: "/gallery?filter=fx",
    imgClass:
      "absolute inset-0 w-full h-full object-cover object-[70%_center] scale-105 group-hover:scale-110 transition-transform duration-[1500ms] ease-out",
  },
  {
    key: "full",
    title: "End-to-End Event Production",
    short: "Full Production",
    desc: "DJ, AV, décor, and special effects under one roof — we coordinate the entire production so you can be a guest at your own event.",
    icon: PartyPopper,
    img: "https://images.pexels.com/photos/18238120/pexels-photo-18238120.jpeg",
    span: "md:col-span-4 md:row-span-1",
  },
];

function ServiceCard({ s, i }) {
  const Icon = s.icon;
  const content = (
    <>
      <img
        src={s.img}
        alt={s.title}
        className={
          s.imgClass ||
          "absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-[1500ms] ease-out"
        }
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />
      <div className="absolute inset-0 bg-[#070707]/10 group-hover:bg-[#070707]/0 transition-colors duration-500" />

      <div className="relative z-10 h-full flex flex-col justify-between p-7 md:p-9">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full flex items-center justify-center bg-white/5 border border-white/10 group-hover:bg-[#FFFFFF] group-hover:border-[#FFFFFF] transition-all duration-500">
              <Icon
                className="h-5 w-5 text-[#FFFFFF] group-hover:text-black transition-colors"
                strokeWidth={1.5}
              />
            </div>
            <span className="text-[11px] uppercase tracking-[0.24em] text-white/50">
              {s.short}
            </span>
          </div>
          <ArrowUpRight
            className="h-5 w-5 text-white/40 group-hover:text-[#FFFFFF] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500"
            strokeWidth={1.5}
          />
        </div>

        <div>
          <h3 className="font-serif text-2xl md:text-3xl lg:text-4xl text-white leading-tight tracking-tight max-w-md">
            {s.title}
          </h3>
          <p className="mt-4 text-sm md:text-base text-white/65 leading-relaxed max-w-lg">
            {s.desc}
          </p>
          {s.to && (
            <div className="mt-5 text-xs tracking-[0.22em] uppercase text-white/80 group-hover:text-white transition-colors">
              {s.to.startsWith("/dj") ? "View DJ profile →" : "View in gallery →"}
            </div>
          )}
        </div>
      </div>
    </>
  );

  const wrapperClass = `group relative overflow-hidden rounded-sm border border-white/5 hover:border-[#FFFFFF]/40 transition-all duration-500 min-h-[340px] md:min-h-[420px] ${s.span} ${s.to ? "cursor-pointer" : ""}`;

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: i * 0.08 }}
      data-testid={`service-card-${s.key}`}
      className={wrapperClass}
    >
      {s.to ? (
        <Link
          to={s.to}
          data-testid={`service-link-${s.key}`}
          className="absolute inset-0 block"
          aria-label={`View ${s.short} gallery`}
        >
          {content}
        </Link>
      ) : (
        content
      )}
    </motion.article>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-[#070707]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 md:mb-20">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10 bg-[#FFFFFF]" />
              <span className="text-xs uppercase tracking-[0.28em] text-[#FFFFFF]">
                What We Do
              </span>
            </div>
            <h2
              data-testid="services-headline"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] text-white tracking-tight max-w-2xl"
            >
              Every event is different — so is{" "}
              <span className="italic text-[#FFFFFF]">every package</span> we
              build.
            </h2>
          </div>
          <p className="text-white/60 max-w-sm text-base leading-relaxed">
            Custom event experiences, your way—choose exactly what you need, or
            let us curate and run the entire night from start to finish.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 auto-rows-[minmax(340px,auto)]">
          {services.map((s, i) => (
            <ServiceCard key={s.key} s={s} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
